import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import {
  BehaviorSubject,
  catchError,
  Observable,
  of,
  take,
  tap,
  throwError,
} from 'rxjs';
import { LoginStage } from '../../non-authenticated/enums/login-stage';
import { LoginRequest } from '../models/login/login-request';
import { LoginRequestBy2FA } from '../models/login/login-request-by2-fa';
import { OtpOption } from '../models/login/otp-option';
import { UserInitializationData } from '../models/login/user-initialization-data';
import { NotificationsService } from './notifications.service';
import { environment } from '../../../environments/environment';
import { LanguageSwitcherService } from './language-switcher.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ValidationRequest } from '../models/login/validation-request';
import { CookieService } from 'ngx-cookie-service';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  // Store the redirect URL
  public redirectUrl!: string;

  public apiUrl: string = environment.apiUrl;
  public validationRequest!: ValidationRequest;
  public loginStage: BehaviorSubject<LoginStage> =
    new BehaviorSubject<LoginStage>(LoginStage.InputCredentials);
  public isUserAuthenticated: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  public isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );

  constructor(
    private notificationService: NotificationsService,
    private router: Router,
    private jwtHelper: JwtHelperService,
    private cookieService: CookieService,
    private languageService: LanguageSwitcherService,
    private route: ActivatedRoute,
    private httpClient: HttpClient,
  ) {
    this.isUserAuthenticated.next(this.isAuthenticated());
  }

  public get2FAMethods(): OtpOption[] {
    return this.validationRequest.OtpOptions;
  }

  public getLoginStage(): BehaviorSubject<LoginStage> {
    return this.loginStage;
  }

  public setLoginStage(loginStage: LoginStage): void {
    this.loginStage.next(loginStage);
  }

  // CURRENTLY NOT USED TODO: REMOVE
  /* Puts the user to the next login stage
   * If the user is already in the last stage, it does nothing.
   */
  public nextLoginStage(): void {
    if (this.loginStage.value !== LoginStage.Input2FA) {
      this.loginStage.next(this.loginStage.value + 1);
    }
  }

  /* Returns the user to the previous login stage
   * If the user is already in the first stage, it does nothing.
   */
  public previousLoginStage(): void {
    if (this.loginStage.value === LoginStage.Select2FA) {
      this.logout();
    } else if (this.loginStage.value === LoginStage.Input2FA) {
      this.loginStage.next(this.loginStage.value - 1);
    }
  }

  /* Resets the login stage to the first stage (InputCredentials) */
  public reset(): void {
    this.loginStage.next(LoginStage.InputCredentials);
  }

  /*
   * This method is used to verify the user's credentials. Step 1 of the login process.
   * If the user has 2FA enabled, it will return the 2FA options,
   * which will be used to select the 2FA method in the next Login Stage.
   * If the user does not have 2FA enabled it returns the user's initialization data.
   * @param loginRequest The user's credentials.
   * @returns ValidationRequestGuid | UserInitializationData
   */
  public verifyLoginCredentials(
    loginRequest: LoginRequest,
  ): Observable<ValidationRequest | UserInitializationData> {
    const url = `${this.apiUrl}api/v2/User/login`;

    this.isLoading.next(true);

    return this.httpClient
      .post<ValidationRequest>(url, loginRequest, {
        withCredentials: true,
      })
      .pipe(
        tap((response) =>
          this.handleResponse(response).pipe(take(1)).subscribe(),
        ),
        catchError((error: HttpErrorResponse) => this.handleError(error)),
        tap(() => this.isLoading.next(false)),
      );
  }

  private handleResponse(
    response: ValidationRequest,
  ): Observable<ValidationRequest | UserInitializationData> {
    this.validationRequest = response;

    if (response.MustResetPassword) {
      this.loginStage.next(LoginStage.MustResetPassword);
      void this.router.navigate(
        [
          `login/reset-password/confirm-reset-password/${response.ResetPasswordToken}`,
        ],
        { replaceUrl: true },
      );
      return of(response);
    } else if (response.IsOtpRequired) {
      this.loginStage.next(LoginStage.Select2FA);
      void this.router.navigate(['login/select2fa']);
      return of(response);
    } else {
      return this.validateLoginCredentials(response);
    }
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    this.loginStage.next(LoginStage.InputCredentials);
    this.isLoading.next(false);

    if (error.status === 401) {
      const message = error.error; // Assuming the error response is a string or JSON object

      if (message === 'ERROR.notAuthorizedForLiteDM') {
        this.notificationService.showError('ERROR.notAuthorizedForLiteDM');
      } else if (message === 'ERROR.mustResetPassword') {
        this.notificationService.showError('ERROR.mustResetPassword');
      } else {
        void this.notificationService.showNotAuthorizedNotification();
      }
    }

    return throwError(() => new Error('An error occurred'));
  }

  /*
   * This method is used to validate the users credentials. Step 2 of the login process.
   * If the user's 2FA code is valid, it returns the user's initialization data.
   * @param code The user's 2FA code.
   * @returns UserInitializationData
   * @throws Error
   */
  public validateLoginCredentials(
    credentials: ValidationRequest,
  ): Observable<UserInitializationData> {
    this.isLoading.next(true);
    const url = `${this.apiUrl}api/v2/User/ValidateSecurityCode`;

    return this.httpClient
      .post<UserInitializationData>(url, credentials, {
        withCredentials: true,
      })
      .pipe(
        tap((data) => this.handleAuthenticationSuccess(data)),
        catchError((error) => this.handleAuthenticationError(error)),
        tap(() => this.isLoading.next(false)),
      );
  }

  private handleAuthenticationSuccess(data: UserInitializationData): void {
    this.isUserAuthenticated.next(true);
    this.notificationService.showLoginSuccessful();
    this.languageService.initializeActiveLanguageAfterLogin(data.LanguageISO);

    this.route.queryParams.pipe(take(1)).subscribe((params) => {
      this.redirectUrl = (params['return'] || '/dashboard/overview') as string;
    });

    if (!data.HasCompletedOnboarding) {
      this.redirectUrl = '/onboarding';
    }

    void this.router.navigateByUrl(this.redirectUrl);
  }

  private handleAuthenticationError(error: any): Observable<never> {
    this.isLoading.next(false);

    if (error.status === 401) {
      this.notificationService.show2FACodeSentIsInvalid();
    }

    return throwError(
      () =>
        new Error(
          'An error occurred in handling the final step of the authentication',
        ),
    );
  }

  takeUserToAuthentication() {
    return this.validateLoginCredentials(this.validationRequest);
  }
  /*
   * This method is used login the user with the received 2FA Code
   * It creates a new object of the validationRequest,
   * and adds to it the received 2FA code.
   * @returns void
   * */
  public loginWith2FaCode(code: number) {
    const copyOfValidationRequest = { ...this.validationRequest };
    copyOfValidationRequest.ValidationKey = code.toString();

    this.validateLoginCredentials(copyOfValidationRequest)
      .pipe(take(1))
      .subscribe();
  }

  /*
   * This method is used to send the user's 2FA code by email.
   * @returns void
   * @throws Error
   * */
  public async Send2FACodeByEmail() {
    const url = this.apiUrl + 'api/v2/User/SendSecurityCodeByEmail';

    const LoginRequestBy2FA: LoginRequestBy2FA = {
      Code: this.validationRequest.UserLoginGuid,
    };

    const myHeaders = new Headers();

    myHeaders.append('Content-Type', 'application/json');
    // myHeaders.append('Allow-Control-Allow-Origin', '*');

    const request: Request = new Request(url, {
      method: 'POST',
      headers: myHeaders,
      body: JSON.stringify(LoginRequestBy2FA),
    });

    const response = await fetch(request);

    if (response.status === 200) {
      this.notificationService.show2FACodeSentSuccesfully();
      this.loginStage.next(LoginStage.Input2FA);
      void this.router.navigateByUrl('login/input2fa?method=email');
    } else {
      this.notificationService.show2FACodeSentFailed();
    }
  }

  /*
   * This method is used to send the user's 2FA code by SMS.
   * @returns void
   * @throws Error
   * */
  public async Send2FACodeBySMS() {
    const url = this.apiUrl + 'api/v2/User/SendSecurityCodeBySMS';

    const LoginRequestBy2FA: LoginRequestBy2FA = {
      Code: this.validationRequest.UserLoginGuid,
    };

    const myHeaders = new Headers();

    myHeaders.append('Content-Type', 'application/json');
    // myHeaders.append('Allow-Control-Allow-Origin', '*');

    const request: Request = new Request(url, {
      method: 'POST',
      headers: myHeaders,
      body: JSON.stringify(LoginRequestBy2FA),
    });

    const response = await fetch(request);

    if (response.status === 200) {
      this.notificationService.show2FACodeSentSuccesfully();
      this.loginStage.next(LoginStage.Input2FA);
      void this.router.navigateByUrl('login/input2fa?method=sms');
    } else {
      this.notificationService.show2FACodeSentFailed();
    }
  }

  /*
   * This method is used to send the 2FA code by the select user's option.
   * @param option The selected 2FA method.
   * @returns void
   * */
  public loginWithSelected2FaOption(option: TwoFaOption): void {
    switch (option) {
      case TwoFaOption.Email:
        this.Send2FACodeByEmail();
        break;
      case TwoFaOption.Authenticator:
        this.loginStage.next(LoginStage.Input2FA);
        this.router.navigateByUrl('login/input2fa?method=authenticator');

        break;
      case TwoFaOption.SMS:
        this.Send2FACodeBySMS();
        break;
      default:
        break;
    }
  }

  /**
   * Validates a token by making a GET request to the server.
   * @param token - The token to be validated.
   * @returns An Observable that emits a ResultResponse object.
   */
  validateToken(token: string): Observable<UserInitializationData> {
    const url = this.apiUrl + 'api/v2/User/ValidateFastLoginToken';

    return this.httpClient
      .post<UserInitializationData>(url, { token }, { withCredentials: true })
      .pipe(
        tap((user) => {
          if (user?.Useremail) {
            this.notificationService.showLoginSuccessful();
            this.isUserAuthenticated.next(true);
            this.languageService.initializeActiveLanguageAfterLogin(
              user.LanguageISO,
            );
          }
        }),
      );
  }

  /*
   * This method is used to check if the user is authenticated.
   * It checks if the access token cookie is expired.
   * @returns boolean
   * */
  public isAuthenticated(): boolean {
    const token = this.cookieService.get('access_token');

    return !this.jwtHelper.isTokenExpired(token);
  }

  /*
   * This method is used to logout the user.
   * It also deletes the access token cookie
   * and redirects the user to the login page.
   * @returns void
   */
  prepareLogout(): void {
    this.isUserAuthenticated.next(false);
    this.cookieService.delete(
      'access_token',
      '/',
      '.dynamicmanaging.com',
      true,
      'Lax',
    ); // Redirect to login page and refresh the page

    this.cookieService.delete('access_token', '/', 'localhost');
    this.router.navigate(['/login'], { replaceUrl: true });
    this.loginStage.next(LoginStage.InputCredentials);
  }

  public logout(): void {
    this.prepareLogout();
    window.location.reload();
  }
}

// This enum is used to select the 2FA method
enum TwoFaOption {
  Email = 1,
  Authenticator = 2,
  SMS = 3,
}
